"use client"

import type { FC } from "react"
import { useMemo } from "react"
import hljs from "highlight.js/lib/core"
import bash from "highlight.js/lib/languages/bash"
import javascript from "highlight.js/lib/languages/javascript"
import json from "highlight.js/lib/languages/json"
import python from "highlight.js/lib/languages/python"

import { cn } from "@productlane/lib/src/cn"

// Only the four languages the reference prints, rather than the full common bundle.
hljs.registerLanguage("bash", bash)
hljs.registerLanguage("javascript", javascript)
hljs.registerLanguage("json", json)
hljs.registerLanguage("python", python)

export type CodeLanguage = "bash" | "javascript" | "json" | "python" | "text"

// Own palette per theme; `!` beats the github-dark sheet the portal loads globally for article code.
export const CODE_THEME = [
	"[--code-fg:#2f3337] [--code-key:#116329] [--code-str:#0a3069] [--code-num:#0550ae] [--code-kw:#cf222e] [--code-fn:#8250df] [--code-var:#953800] [--code-muted:#6e7781]",
	"dark:[--code-fg:#c5cad3] dark:[--code-key:#8ddb8c] dark:[--code-str:#96d0ff] dark:[--code-num:#6cb6ff] dark:[--code-kw:#f47067] dark:[--code-fn:#dcbdfb] dark:[--code-var:#f69d50] dark:[--code-muted:#768390]",
	"text-[color:var(--code-fg)]",
	"[&_.hljs-attr]:!text-[color:var(--code-key)] [&_.hljs-property]:!text-[color:var(--code-key)]",
	"[&_.hljs-string]:!text-[color:var(--code-str)] [&_.hljs-regexp]:!text-[color:var(--code-str)]",
	"[&_.hljs-number]:!text-[color:var(--code-num)] [&_.hljs-literal]:!text-[color:var(--code-num)]",
	"[&_.hljs-keyword]:!text-[color:var(--code-kw)] [&_.hljs-type]:!text-[color:var(--code-kw)]",
	"[&_.hljs-title]:!text-[color:var(--code-fn)]",
	"[&_.hljs-built_in]:!text-[color:var(--code-var)] [&_.hljs-variable]:!text-[color:var(--code-var)] [&_.hljs-template-variable]:!text-[color:var(--code-var)] [&_.hljs-symbol]:!text-[color:var(--code-var)]",
	"[&_.hljs-comment]:!text-[color:var(--code-muted)] [&_.hljs-meta]:!text-[color:var(--code-muted)]",
	"[&_.hljs-operator]:!text-[color:var(--code-fg)] [&_.hljs-subst]:!text-[color:var(--code-fg)] [&_.hljs-punctuation]:!text-[color:var(--code-fg)]",
].join(" ")

function escapeHtml(text: string): string {
	return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

export const HighlightedCode: FC<{
	code: string
	language: CodeLanguage
	className?: string
}> = ({ code, language, className }) => {
	const html = useMemo(() => {
		if (language === "text") return escapeHtml(code)
		try {
			return hljs.highlight(code, { language, ignoreIllegals: true }).value
		} catch {
			return escapeHtml(code)
		}
	}, [code, language])

	return (
		<pre
			className={cn(
				"overflow-auto px-4 py-3.5 font-mono text-[12.5px] font-[400] leading-[1.7] [tab-size:2]",
				CODE_THEME,
				className,
			)}
		>
			<code
				// hljs escapes the source it tokenizes; escapeHtml covers the fallback.
				dangerouslySetInnerHTML={{ __html: html }}
			/>
		</pre>
	)
}

export function exampleLanguage(example: string): CodeLanguage {
	try {
		JSON.parse(example)
		return "json"
	} catch {
		return "text"
	}
}
